import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import classNames from 'classnames';
import ContentChatIcon from '../../../BuyerCopilot/ContentChatIcon';
import { Referrers } from '../../../../enums/Analytics';
import { useI18n } from '../../../../lib/hook/useI18n';
import AnonymousIdentityGuard from '../../View/AnonymousIdentityGuard';
import { DeepLinkCopyIconButton } from '../Edit/EditableDeepLinkActionButtons';
import { useBuyerContentAssetActionsStyles } from './buyerContentAssetActionsStyles';

export type BuyerContentAssetActionsVariant =
  | 'card-copilot'
  | 'card-copy'
  | 'tile';

export interface BuyerContentAssetActionsProps {
  hasHeader?: boolean;
  onCopilotClick?: () => void;
  onCopyLink?: (sectionId: string) => void;
  sectionId: string;
  showCopilot: boolean;
  showCopy: boolean;
  variant: BuyerContentAssetActionsVariant;
}

const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

const BuyerContentAssetActions: React.FC<BuyerContentAssetActionsProps> = ({
  hasHeader,
  onCopilotClick,
  onCopyLink,
  sectionId,
  showCopilot,
  showCopy,
  variant,
}) => {
  const classes = useBuyerContentAssetActionsStyles();
  const { t } = useI18n();

  const copilotButton = useMemo(() => {
    if (!showCopilot || !onCopilotClick) {
      return null;
    }
    return (
      <AnonymousIdentityGuard referrer={Referrers.DEAL_ROOMS.BUYER_COPILOT}>
        <ContentChatIcon
          tooltipTitle={t('buyerCopilot.content.ask_copilot')}
          onClick={(e) => {
            e.preventDefault();
            stopPropagation(e);
            onCopilotClick();
          }}
        />
      </AnonymousIdentityGuard>
    );
  }, [onCopilotClick, showCopilot, t]);

  const copyButton = useMemo(() => {
    if (!showCopy || !onCopyLink) {
      return null;
    }
    return (
      <DeepLinkCopyIconButton
        ariaLabel={t('rooms.editor.copy_link_to_content')}
        iconButtonSize="small"
        onClick={() => onCopyLink(sectionId)}
      />
    );
  }, [onCopyLink, sectionId, showCopy, t]);

  const hasContent = useMemo(() => {
    if (variant === 'card-copy') {
      return !!copyButton;
    }
    if (variant === 'card-copilot') {
      return !!copilotButton;
    }
    return !!(copyButton || copilotButton);
  }, [copilotButton, copyButton, variant]);

  const wrapperClassName = useMemo(
    () =>
      classNames(classes.root, {
        [classes.cardCopy]: variant === 'card-copy',
        [classes.cardCopilot]: variant === 'card-copilot',
        [classes.tile]: variant === 'tile',
      }),
    [classes.cardCopy, classes.cardCopilot, classes.root, classes.tile, variant]
  );

  const wrapperSx = useMemo(
    () => (variant === 'card-copilot' ? { top: hasHeader ? 8 : 0 } : undefined),
    [hasHeader, variant]
  );

  const actionsContent = useMemo(() => {
    if (variant === 'tile') {
      return (
        <>
          {copyButton}
          {copilotButton}
        </>
      );
    }
    if (variant === 'card-copy') {
      return copyButton;
    }
    return copilotButton;
  }, [copilotButton, copyButton, variant]);

  if (!hasContent) {
    return null;
  }

  return (
    <Box
      className={wrapperClassName}
      onClick={variant !== 'card-copilot' ? stopPropagation : undefined}
      sx={wrapperSx}
    >
      {actionsContent}
    </Box>
  );
};

export default BuyerContentAssetActions;
